import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Avatar,
  Tabs,
  Tab,
  Switch,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import {
  Edit,
  Block,
  CheckCircle,
} from '@mui/icons-material';
import { User, UserRole, Chantier } from '@/types';
import { UserService } from '@/services/userService';
import { AuthService } from '@/services/auth';
import { format } from 'date-fns';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '@/services/firebase';

const UserManagementPage: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [users, setUsers] = useState<User[]>([]);
  const [chantiers, setChantiers] = useState<Chantier[]>([]);
  const [promoteurs, setPromoteurs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingChantiers, setLoadingChantiers] = useState(false);
  const [loadingPromoteurs, setLoadingPromoteurs] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
    loadChantiers();
    loadPromoteurs();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await UserService.listUsers();
      if (response.success && response.data) {
        setUsers(response.data);
      } else {
        setError(response.error || 'Failed to load users');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while loading users');
    } finally {
      setLoading(false);
    }
  };

  const loadChantiers = async () => {
    try {
      setLoadingChantiers(true);
      const snapshot = await getDocs(collection(db, 'chantiers'));
      setChantiers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Chantier)));
    } catch (err: any) {
      setError('Failed to load chantiers');
    } finally {
      setLoadingChantiers(false);
    }
  };

  const loadPromoteurs = async () => {
    try {
      setLoadingPromoteurs(true);
      const snapshot = await getDocs(collection(db, 'promoteurs'));
      setPromoteurs(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err: any) {
      setError('Failed to load promoteurs');
    } finally {
      setLoadingPromoteurs(false);
    }
  };

  const handleStatusToggle = async (user: User) => {
    try {
      const newStatus = user.status === 'active' ? 'inactive' : 'active';
      await UserService.setUserStatus(user.id, newStatus as any);
      setUsers(users.map(u => u.id === user.id ? { ...u, status: newStatus as any } : u));
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleRoleChange = async (user: User, newRole: UserRole) => {
    try {
      const defaultPerms = AuthService.getDefaultPermissions(newRole);
      await UserService.updateUserRole(user.id, newRole, defaultPerms);
      setUsers(users.map(u => u.id === user.id ? { ...u, role: newRole, permissions: defaultPerms } : u));
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleToggleFeatured = async (chantier: Chantier) => {
    try {
      const newValue = !chantier.isFeatured;
      await updateDoc(doc(db, 'chantiers', chantier.id), { isFeatured: newValue });
      setChantiers(chantiers.map(c => c.id === chantier.id ? { ...c, isFeatured: newValue } : c));
    } catch (err: any) {
      alert(`Error updating chantier: ${err.message}`);
    }
  };

  const handleTogglePromoteurStatus = async (promoteur: any) => {
    try {
      const newStatus = promoteur.status === 'active' ? 'inactive' : 'active';
      await updateDoc(doc(db, 'promoteurs', promoteur.id), {
        status: newStatus,
        updatedAt: new Date()
      });
      setPromoteurs(promoteurs.map(p => p.id === promoteur.id ? { ...p, status: newStatus } : p));
    } catch (err: any) {
      alert(`Error updating promoteur: ${err.message}`);
    }
  };

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN: return 'error';
      case UserRole.AGENT: return 'primary';
      case UserRole.CLIENT: return 'success';
      default: return 'default';
    }
  };

  if (loading && tabIndex === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Platform Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage system users, roles, and featured content
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Paper sx={{ mb: 4 }}>
        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)}>
          <Tab label="Users" />
          <Tab label="Featured Content" />
          <Tab label="Promoteurs" />
        </Tabs>
      </Paper>

      {tabIndex === 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar src={user.profile.avatar || undefined}>
                        {user.profile.firstName[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {user.profile.firstName} {user.profile.lastName}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{user.profile.email}</TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={user.role}
                        onChange={(e) => handleRoleChange(user, e.target.value as UserRole)}
                        sx={{ 
                          fontSize: '0.8125rem',
                          '& .MuiSelect-select': { py: 0.5 }
                        }}
                      >
                        {Object.values(UserRole).map((role) => (
                          <MenuItem key={role} value={role} sx={{ textTransform: 'capitalize' }}>
                            {role}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={user.status} 
                      size="small" 
                      variant="outlined"
                      color={user.status === 'active' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {user.createdAt ? format((user.createdAt as any).toDate(), 'MMM dd, yyyy') : 'N/A'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      size="small" 
                      color={user.status === 'active' ? 'error' : 'success'}
                      onClick={() => handleStatusToggle(user)}
                    >
                      {user.status === 'active' ? <Block fontSize="small" /> : <CheckCircle fontSize="small" />}
                    </IconButton>
                    <IconButton size="small">
                      <Edit fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tabIndex === 1 && (
        <TableContainer component={Paper}>
          {loadingChantiers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Chantier Name</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell align="right">Featured</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {chantiers.map((chantier) => (
                  <TableRow key={chantier.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {chantier.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{chantier.location?.city || 'N/A'}</TableCell>
                    <TableCell align="right">
                      <Switch 
                        checked={!!chantier.isFeatured} 
                        onChange={() => handleToggleFeatured(chantier)} 
                        color="primary" 
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {chantiers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">No chantiers found.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      )}

      {/* Tab 3: Promoteurs */}
      {tabIndex === 2 && (
        <TableContainer component={Paper}>
          {loadingPromoteurs ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Promoteur</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Properties</TableCell>
                  <TableCell>Leads</TableCell>
                  <TableCell>Verified</TableCell>
                  <TableCell align="right">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {promoteurs.map((promoteur) => (
                  <TableRow key={promoteur.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {promoteur.logo && (
                          <Avatar src={promoteur.logo} variant="rounded" sx={{ width: 48, height: 48 }}>
                            {promoteur.name[0]}
                          </Avatar>
                        )}
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {promoteur.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {promoteur.location?.primaryCity || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{promoteur.contactInfo?.email || 'N/A'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {promoteur.contactInfo?.phones?.[0] || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={promoteur.stats?.totalProperties || 0}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={promoteur.stats?.totalLeads || 0}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={promoteur.verified ? 'Verified' : 'Not Verified'}
                        size="small"
                        color={promoteur.verified ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                        <Chip
                          label={promoteur.status === 'active' ? 'Active' : 'Disabled'}
                          size="small"
                          color={promoteur.status === 'active' ? 'success' : 'default'}
                        />
                        <Switch
                          checked={promoteur.status === 'active'}
                          onChange={() => handleTogglePromoteurStatus(promoteur)}
                          color="success"
                          size="small"
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {promoteurs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary">No promoteurs found.</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Run the scraping function to import promoteurs from Avito.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      )}
    </Box>
  );
};

export default UserManagementPage;
